import React, { useState, useEffect, useRef } from 'react';
import {
    getLeaseAgreementsRent,
    insertRentalPaymentCase,
    checkPaymentStatusNoAuth,
    updatePaymentStatus,
    getPublicLeaseBillings,
} from '../services/api';
import './RentalServicePage.css'; 

// 1. Import Icons from Lucide
import { 
    House, 
    Phone, 
    Mail, 
    CheckCircle, 
    X, 
    Loader2, User, MapPin, Building2, DoorOpen, CreditCard, Calendar, Minus, Plus, ArrowRight
} from 'lucide-react';

// --- Payment Status & Receipt Modal ---
const PaymentStatusModal = ({ isOpen, onClose, invoiceNumber, caseId }) => {
    const [status, setStatus] = useState('pending'); // pending | complete
    const [receiptUrl, setReceiptUrl] = useState(null);
    const pollTimerRef = useRef(null);

    const pdfBaseUrl = process.env.REACT_APP_ZAMPAYPDF_BASE_URL;

    useEffect(() => {
        if (isOpen && invoiceNumber) {
            setStatus('pending');
            setReceiptUrl(null);
            startPolling();
        }
        return () => stopPolling();
        // eslint-disable-next-line
    }, [isOpen, invoiceNumber]);

    const stopPolling = () => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };

    const startPolling = () => {
        stopPolling();
        pollTimerRef.current = setInterval(async () => {
            try {
                const response = await checkPaymentStatusNoAuth(invoiceNumber);
                const paymentState = response.data?.status || response.data?.paymentStatus;
                
                if (paymentState === 'Paid') {
                    stopPolling();
                    setStatus('complete');
                    const url = `${pdfBaseUrl}/pdf?documentType=receipt&invoiceNumber=${invoiceNumber}#navpanes=0&view=FitH&scrollbar=0`;
                    setReceiptUrl(url);

                    if (caseId) {
                        await updatePaymentStatus(caseId, invoiceNumber).catch(e => console.error(e));
                    }
                }
            } catch (error) {
                console.error("Polling error", error);
            }
        }, 5000); 
    };

    if (!isOpen) return null;

    // 2. Prevent closing by clicking background if pending
    const handleOverlayClick = () => {
        if (status === 'complete') {
            onClose();
        }
    };

    return (
        <div 
            className="modal-overlay" 
            style={{ zIndex: 3000 }} 
            onClick={handleOverlayClick} // Only closes if complete
        >
            <div 
                className={`modal-content status-modal-content ${status === 'complete' ? 'large-modal' : ''}`} 
                onClick={e => e.stopPropagation()}
            >
                {/* 2. Remove Close Button (X) completely during pending state */}
                {status === 'complete' && (
                    <button className="close-btn-simple" onClick={onClose}>
                        <X size={24} />
                    </button>
                )}
                
                {/* STATE 1: PENDING PAYMENT */}
                {status === 'pending' && (
                    <div className="status-container">
                        {/* 1. Use Mail Icon for SMS sent */}
                        <div className="status-icon-wrapper">
                             <Mail className="status-icon-pulse" size={64} color="#3182ce" />
                        </div>
                        <h2>Payment Link Sent</h2>
                        <p>An SMS with the payment link has been sent to your number.</p>
                        <p>Please complete the payment for rental.</p>
                        <div className="loader-bar"></div>
                        <span className="status-text-muted">Waiting for confirmation...</span>
                    </div>
                )}

                {/* STATE 2: PAYMENT COMPLETE / SHOW RECEIPT */}
                {status === 'complete' && !receiptUrl && (
                     <div className="status-container">
                        <CheckCircle size={64} color="#48bb78" />
                        <h2>Payment Successful!</h2>
                        <p>Generating receipt...</p>
                     </div>
                )}

                {/* 3. Make Receipt Modal Big */}
                {status === 'complete' && receiptUrl && (
                    <div className="iframe-container-large">
                        <div className="success-banner">
                            <CheckCircle size={20} style={{marginRight: '8px'}}/> 
                            Payment Complete
                        </div>
                        <iframe src={receiptUrl} title="Receipt" className="pdf-iframe-large" />
                        <button className="checkout-btn" onClick={onClose} style={{marginTop: '15px', backgroundColor: '#48bb78'}}>
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Main Rental Page Component ---
const RentalPaymentPage = () => {
    const RENTAL_SERVICE_ID = 'b5b2fc14-d85b-4a26-92af-8bf445cbc7d2'; 
    
    const [searchTerm, setSearchTerm] = useState('');
    const [leases, setLeases] = useState([]); 
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedLease, setSelectedLease] = useState(null);
    const [monthsToPay, setMonthsToPay] = useState(1);
    const [startMonth, setStartMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [isProcessing, setIsProcessing] = useState(false);

    const [tempPhoneNumber, setTempPhoneNumber] = useState(''); 
    const [isPhoneRequired, setIsPhoneRequired] = useState(false);
    const [tempNRC, setTempNRC] = useState(''); 
    const [isNRCRequired, setIsNRCRequired] = useState(false);

    const [statusModal, setStatusModal] = useState({
        isOpen: false,
        invoiceNumber: null,
        caseId: null
    });

    const [billings, setBillings]               = useState([]);
    const [billingsLoading, setBillingsLoading] = useState(false);

    const handleSearch = async () => {
        setError(null);
        setLeases([]);

        const cleanSearchTerm = searchTerm.replace(/\D/g, ''); 
        if (cleanSearchTerm.length < 10) {
            setError("Please enter a valid phone number.");
            return;
        }

        setLoading(true);
        try {
            const response = await getLeaseAgreementsRent({ 
                phoneNumber: searchTerm, 
                limit: 20 
            }); 
            
            let rawData = [];
            if (response.data && Array.isArray(response.data.leaseAgreements)) {
                rawData = response.data.leaseAgreements;
            } else if (Array.isArray(response.data)) {
                rawData = response.data;
            }

            // --- NORMALIZE DATA HERE ---
            // This ensures the rest of your code (which uses .Tenant, .Property, etc.) works everywhere
            const normalizedData = rawData.map(lease => ({
                ...lease,
                Tenant: lease.Tenant || lease.tenant,
                Property: lease.Property || lease.property,
                Building: lease.Building || lease.building,
                Room: lease.Room || lease.room
            }));

            // Filter using the normalized object and a digits-only comparison
            const filtered = normalizedData.filter(l => {
                const phoneOnRecord = l.Tenant?.phoneNumber || "";
                const cleanPhoneOnRecord = phoneOnRecord.replace(/\D/g, '');
                return cleanPhoneOnRecord.includes(cleanSearchTerm);
            });

            if (filtered.length === 0) {
                setError("No lease found for this phone number.");
            } else {
                setLeases(filtered);
            }

        } catch (err) {
            console.error("Search error:", err);
            setError("Unable to search records. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const getPaymentMonths = () => {
        if (!startMonth) return [];
        const [year, month] = startMonth.split('-').map(Number);
        return Array.from({ length: monthsToPay }, (_, i) => {
            const d = new Date(year, month - 1 + i, 1);
            return {
                name: d.toLocaleString('default', { month: 'long' }).toUpperCase(),
                year: d.getFullYear()
            };
        });
    };

    const handleLeaseClick = async (lease) => {
        setSelectedLease(lease);
        setMonthsToPay(1);
        setBillings([]);
        const now = new Date();
        setStartMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);

        const phone = lease.Tenant?.phoneNumber;
        setTempPhoneNumber(phone || '');
        setIsPhoneRequired(!phone?.trim());

        const nrc = lease.Tenant?.nrc;
        setTempNRC(nrc || '');
        setIsNRCRequired(!nrc?.trim());

        setBillingsLoading(true);
        try {
            const res = await getPublicLeaseBillings(lease.id);
            setBillings(res.data || []);
        } catch (e) {
            console.error('Failed to load billings', e);
        } finally {
            setBillingsLoading(false);
        }
    };

    const calculateTotal = () => {
        if (!selectedLease) return 0;
        return (parseFloat(selectedLease.rentAmount) * monthsToPay).toFixed(2);
    };

    const handleConfirmPayment = async () => {
        if (!selectedLease) return;

        if (isPhoneRequired && !tempPhoneNumber.trim()) {
            alert("Please enter your phone number.");
            return;
        }
        if (isNRCRequired && !tempNRC.trim()) {
            alert("Please enter your NRC number.");
            return;
        }

        setIsProcessing(true);

        try {
            const paymentMonths = getPaymentMonths();
            const payload = {
                serviceId: RENTAL_SERVICE_ID,
                leaseAgreementId: selectedLease.id,
                tenantId: selectedLease.Tenant?.id,
                monthsPaid: monthsToPay,
                startMonth,
                totalAmount: calculateTotal(),
                description: `Rental payment for ${paymentMonths.map(m => `${m.name} ${m.year}`).join(', ')}`,
                phoneNumber: tempPhoneNumber.trim(),
                nrc: tempNRC.trim(),
                name: selectedLease.Tenant?.name,
                email: selectedLease.Tenant?.email || ''
            };

            const response = await insertRentalPaymentCase(payload);
            
            const newCaseId = response.data?.id;
            const newInvoiceNumber = response.data?.case?.invoicenumber || response.data?.invoiceNumber;

            if (newInvoiceNumber) {
                setSelectedLease(null); 
                setStatusModal({
                    isOpen: true,
                    invoiceNumber: newInvoiceNumber,
                    caseId: newCaseId
                });
            } else {
                alert("System Error: Invoice number missing.");
            }

        } catch (err) {
            console.error("Payment initiation failed:", err);
            alert("Failed to initiate payment.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="rental-container">
            <div className="rental-header">
                {/* 1. Use House Icon */}
                <h1>Pay Your Rent <House size={32} style={{marginBottom: '-4px'}} /></h1>
                <p>Enter your full phone number to search for your lease.</p>
                
                <div className="search-wrapper">
                    <input 
                        type="text"
                        placeholder="e.g. 0977123456"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button 
                        className="search-btn" 
                        onClick={handleSearch}
                        disabled={loading}
                    >
                        {loading ? <Loader2 className="animate-spin" size={20}/> : 'Search'}
                    </button>
                </div>
                {error && <p className="error-msg">{error}</p>}
            </div>

            <div className="rental-grid">
                {leases.map(lease => (
                    <div key={lease.id} className="rental-card" onClick={() => handleLeaseClick(lease)}>
                        <div className="rental-card-header">
                            <span className="tenant-badge">👤 {lease.Tenant?.name}</span>
                        </div>
                        
                        <div className="rental-card-content">
                            <p className="location-detail" style={{
                                textAlign:'center', 
                                fontSize:'1.1rem', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                gap: '8px'
                            }}>
                                {/* 1. Use Phone Icon */}
                                <Phone size={18} /> 
                                {lease.Tenant?.phoneNumber || "No Phone on File"}
                            </p>
                            <p className="security-hint">Click to View Property Details & Pay</p>
                        </div>

                        <button className="pay-btn">
                            Select Lease
                        </button>
                    </div>
                ))}
            </div>

            {/* Payment Selection Modal - REDESIGNED */}
            {selectedLease && (
                <div className="modal-overlay" onClick={() => setSelectedLease(null)}>
                    <div className="modal-content payment-selection-modal" onClick={e => e.stopPropagation()}>
                        
                        {/* Header */}
                        <div className="modal-header-modern">
                            <div className="header-text">
                                <h2>Confirm Lease Details</h2>
                                <p>Review details before payment</p>
                            </div>
                            <button className="close-btn-modern" onClick={() => setSelectedLease(null)}>
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="modal-body-scroll">
                            {/* 1. Summary Card */}
                            <div className="lease-summary-card">
                                <div className="summary-item">
                                    <div className="icon-box"><User size={18} /></div>
                                    <div className="info-text">
                                        <span className="label">Tenant Name</span>
                                        <span className="value">{selectedLease.Tenant?.name}</span>
                                    </div>
                                </div>
                                
                                <div className="grid-row">
                                    <div className="summary-item">
                                        <div className="icon-box"><MapPin size={18} /></div>
                                        <div className="info-text">
                                            <span className="label">Property</span>
                                            <span className="value">{selectedLease.Property?.name || "N/A"}</span>
                                        </div>
                                    </div>
                                    <div className="summary-item">
                                        <div className="icon-box"><Building2 size={18} /></div>
                                        <div className="info-text">
                                            <span className="label">Building</span>
                                            <span className="value">{selectedLease.Building?.name || "N/A"}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="summary-item">
                                    <div className="icon-box"><DoorOpen size={18} /></div>
                                    <div className="info-text">
                                        <span className="label">Room / Unit</span>
                                        <span className="value">{selectedLease.Room?.name || "N/A"}</span>
                                    </div>
                                </div>

                                {/* Monthly rent + outstanding balance */}
                                <div className="summary-meta-row">
                                    <div className="summary-meta-item">
                                        <span className="label">Monthly Rent</span>
                                        <span className="value value--green">
                                            K {parseFloat(selectedLease.rentAmount || 0).toLocaleString('en-ZM', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                    <div className="summary-meta-item">
                                        <span className="label">Outstanding Balance</span>
                                        <span className={`value ${parseFloat(selectedLease.balance || 0) > 0 ? 'value--red' : 'value--green'}`}>
                                            K {parseFloat(selectedLease.balance || 0).toLocaleString('en-ZM', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Billing History */}
                            <div className="section-divider">
                                <span>Billing History</span>
                            </div>
                            {billingsLoading ? (
                                <div className="bh-loading">
                                    <Loader2 className="animate-spin" size={15} />
                                    Loading billing records…
                                </div>
                            ) : billings.length === 0 ? (
                                <div className="bh-empty">No billing records found for this lease.</div>
                            ) : (() => {
                                const totalBilled  = billings.reduce((s, b) => s + parseFloat(b.amountDue || 0), 0);
                                const totalPaid    = billings.filter(b => b.status === 'paid').reduce((s, b) => s + parseFloat(b.amountDue || 0), 0);
                                const outstanding  = billings.filter(b => b.status !== 'paid').reduce((s, b) => s + parseFloat(b.amountDue || 0), 0);
                                const fmt = v => `K ${v.toLocaleString('en-ZM', { minimumFractionDigits: 2 })}`;
                                return (
                                    <>
                                        <div className="bh-chips">
                                            <div className="bh-chip bh-chip--total">
                                                <div className="bh-chip__label">Total Billed</div>
                                                <div className="bh-chip__value">{fmt(totalBilled)}</div>
                                            </div>
                                            <div className="bh-chip bh-chip--paid">
                                                <div className="bh-chip__label">Paid</div>
                                                <div className="bh-chip__value">{fmt(totalPaid)}</div>
                                            </div>
                                            <div className="bh-chip bh-chip--outstanding">
                                                <div className="bh-chip__label">Outstanding</div>
                                                <div className="bh-chip__value">{fmt(outstanding)}</div>
                                            </div>
                                        </div>
                                        <div className="bh-table-wrap">
                                            <table className="bh-table">
                                                <thead>
                                                    <tr>
                                                        <th>Month</th>
                                                        <th>Amount</th>
                                                        <th>Due Date</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {billings.map(b => (
                                                        <tr key={b.id}>
                                                            <td className="bh-cell--month">{b.billingMonth}</td>
                                                            <td className="bh-cell--amount">{fmt(parseFloat(b.amountDue || 0))}</td>
                                                            <td className="bh-cell--date">{b.dueDate || '—'}</td>
                                                            <td>
                                                                <span className={`bh-badge bh-badge--${b.status || 'pending'}`}>
                                                                    {b.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                );
                            })()}

                            {/* Required Information — only shown if tenant is missing phone or NRC */}
                            {(isPhoneRequired || isNRCRequired) && (
                                <div className="section-divider"><span>Required Information</span></div>
                            )}
                            <div className="input-section">
                                {isPhoneRequired && (
                                    <div className="modern-input-group">
                                        <label> <Phone size={14} /> Phone Number <span className="req">*</span></label>
                                        <input
                                            type="text"
                                            value={tempPhoneNumber}
                                            onChange={(e) => setTempPhoneNumber(e.target.value)}
                                            placeholder="e.g. 0977123456"
                                            className="modern-input"
                                        />
                                    </div>
                                )}
                                {isNRCRequired && (
                                    <div className="modern-input-group">
                                        <label> <CreditCard size={14} /> NRC Number <span className="req">*</span></label>
                                        <input
                                            type="text"
                                            value={tempNRC}
                                            onChange={(e) => setTempNRC(e.target.value)}
                                            placeholder="e.g. 123456/10/1"
                                            className="modern-input"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* 4. Payment Duration */}
                            <div className="duration-section">
                                <label className="section-label"><Calendar size={16}/> Payment Duration</label>

                                <div className="duration-grid">
                                    <div className="duration-card">
                                        <span className="duration-card-label">Starting Month</span>
                                        <input
                                            type="month"
                                            value={startMonth}
                                            onChange={(e) => setStartMonth(e.target.value)}
                                            className="month-picker-input"
                                        />
                                    </div>

                                    <div className="duration-card">
                                        <span className="duration-card-label">No. of Months</span>
                                        <div className="stepper-compact">
                                            <button
                                                className="step-btn"
                                                onClick={() => setMonthsToPay(Math.max(1, monthsToPay - 1))}
                                            >
                                                <Minus size={16} />
                                            </button>
                                            <span className="step-num-compact">{monthsToPay}</span>
                                            <button
                                                className="step-btn"
                                                onClick={() => setMonthsToPay(monthsToPay + 1)}
                                            >
                                                <Plus size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="payment-months-preview">
                                    <div className="preview-header">
                                        <span className="months-preview-label">Paying for:</span>
                                        <span className="preview-total">ZMW {calculateTotal()}</span>
                                    </div>
                                    <div className="month-pills">
                                        {getPaymentMonths().map((m, i) => (
                                            <span key={i} className="month-pill">
                                                {m.name} {m.year}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* 5. Action Button */}
                            <button 
                                className="checkout-btn-modern full-width" 
                                onClick={handleConfirmPayment}
                                disabled={isProcessing}
                            >
                                {isProcessing ? (
                                    <>
                                        <Loader2 className="animate-spin" size={20} style={{marginRight: '8px'}}/> 
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        Proceed to Payment <ArrowRight size={20} style={{marginLeft: '8px'}}/>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <PaymentStatusModal 
                isOpen={statusModal.isOpen}
                onClose={() => {
                    setStatusModal({ ...statusModal, isOpen: false });
                    setLeases([]); 
                    setSearchTerm('');
                }}
                invoiceNumber={statusModal.invoiceNumber}
                caseId={statusModal.caseId}
            />
        </div>
    );
};

export default RentalPaymentPage;